import { vec3 } from 'gl-matrix';
import getVoxelThicknessAlongNormal from './getVoxelThicknessAlongNormal.js';
import { getSlabEpsilon, getSlabHalfWidth, isSlabDepthLowInclusive, resolveReferencePlaneThickness, } from './slabMembership.js';
export function getIndexSpaceNormal(volume, normal) {
    const { direction, spacing } = volume;
    const iVector = direction.slice(0, 3);
    const jVector = direction.slice(3, 6);
    const kVector = direction.slice(6, 9);
    return [
        spacing[0] * vec3.dot(iVector, normal),
        spacing[1] * vec3.dot(jVector, normal),
        spacing[2] * vec3.dot(kVector, normal),
    ];
}
export function pickOuterAxis(g) {
    const magnitudes = [Math.abs(g[0]), Math.abs(g[1]), Math.abs(g[2])];
    let best = 0;
    for (const axis of [1, 2]) {
        if (magnitudes[axis] > magnitudes[best]) {
            best = axis;
        }
    }
    return best;
}
export function buildIndexSpaceSlab(volume, planePoint, normal, referencePlaneThickness, options = {}) {
    const { origin } = volume;
    const g = getIndexSpaceNormal(volume, normal);
    const voxelThickness = getVoxelThicknessAlongNormal(volume, normal);
    const thickness = resolveReferencePlaneThickness(referencePlaneThickness, voxelThickness);
    const c0 = (origin[0] - planePoint[0]) * normal[0] +
        (origin[1] - planePoint[1]) * normal[1] +
        (origin[2] - planePoint[2]) * normal[2];
    const rawHalfWidth = getSlabHalfWidth(thickness, voxelThickness, options.depthCoverage);
    const epsilon = getSlabEpsilon(voxelThickness);
    const halfOpen = isSlabDepthLowInclusive(options.depthCoverage);
    const depthLow = halfOpen ? -rawHalfWidth - epsilon : -rawHalfWidth + epsilon;
    const depthHigh = rawHalfWidth - epsilon;
    const halfWidth = halfOpen ? rawHalfWidth + epsilon : rawHalfWidth - epsilon;
    const outerAxis = pickOuterAxis(g);
    const remaining = [0, 1, 2].filter((axis) => axis !== outerAxis);
    let columnAxis = remaining[0];
    let rowAxis = remaining[1];
    if (options.columnAxis !== undefined) {
        if (options.columnAxis === outerAxis) {
            throw new Error(`columnAxis ${options.columnAxis} cannot also be the outer axis`);
        }
        columnAxis = options.columnAxis;
        rowAxis = remaining[0] === columnAxis ? remaining[1] : remaining[0];
    }
    return {
        g,
        c0,
        voxelThickness,
        referencePlaneThickness: thickness,
        halfWidth,
        depthLow,
        depthHigh,
        halfOpen,
        outerAxis,
        rowAxis,
        columnAxis,
    };
}
export function depthAtIndex(slab, ijk) {
    const { g, c0 } = slab;
    return ijk[0] * g[0] + ijk[1] * g[1] + ijk[2] * g[2] + c0;
}
function integersWithProductInOpenInterval(coeff, lo, hi) {
    if (lo >= hi) {
        return null;
    }
    if (coeff === 0) {
        return lo < 0 && 0 < hi ? [-Infinity, Infinity] : null;
    }
    const a = lo / coeff;
    const b = hi / coeff;
    const lowExclusive = coeff > 0 ? a : b;
    const highExclusive = coeff > 0 ? b : a;
    const min = Math.floor(lowExclusive) + 1;
    const max = Math.ceil(highExclusive) - 1;
    return min > max ? null : [min, max];
}
function integersWithProductInHalfOpenInterval(coeff, lo, hi) {
    if (lo >= hi) {
        return null;
    }
    if (coeff === 0) {
        return lo <= 0 && 0 < hi ? [-Infinity, Infinity] : null;
    }
    const a = lo / coeff;
    const b = hi / coeff;
    const min = coeff > 0 ? Math.ceil(a) : Math.floor(b) + 1;
    const max = coeff > 0 ? Math.ceil(b) - 1 : Math.floor(a);
    return min > max ? null : [min, max];
}
function clampRange(range, clampTo) {
    if (!range) {
        return null;
    }
    if (!clampTo) {
        return Number.isFinite(range[0]) && Number.isFinite(range[1])
            ? range
            : null;
    }
    const min = Math.max(range[0], clampTo[0]);
    const max = Math.min(range[1], clampTo[1]);
    return min > max ? null : [min, max];
}
export function getDepthRun(slab, outerIndex, rowIndex, clampTo) {
    const { g, c0, halfWidth, depthLow, depthHigh, halfOpen, outerAxis, rowAxis, columnAxis, } = slab;
    if (!(halfWidth > 0)) {
        return null;
    }
    const base = outerIndex * g[outerAxis] + rowIndex * g[rowAxis] + c0;
    const solve = halfOpen
        ? integersWithProductInHalfOpenInterval
        : integersWithProductInOpenInterval;
    return clampRange(solve(g[columnAxis], depthLow - base, depthHigh - base), clampTo);
}
export function getSlabAxisBound(slab, axis, bounds, fixed) {
    const { g, c0, halfWidth } = slab;
    if (!(halfWidth > 0)) {
        return null;
    }
    let slackLow = 0;
    let slackHigh = 0;
    let base = c0;
    for (const other of [0, 1, 2]) {
        if (other === axis) {
            continue;
        }
        if (fixed && other === fixed.axis) {
            base += fixed.index * g[other];
            continue;
        }
        const contributions = [
            bounds[other][0] * g[other],
            bounds[other][1] * g[other],
        ];
        slackLow += Math.min(contributions[0], contributions[1]);
        slackHigh += Math.max(contributions[0], contributions[1]);
    }
    return clampRange(integersWithProductInOpenInterval(g[axis], -halfWidth - slackHigh - base, halfWidth - slackLow - base), bounds[axis]);
}
